/**
 * This file is part of ONP.
 *
 * Copyright (c) Opensoft (http://opensoftdev.com)
 *
 * The unauthorized use of this code outside the boundaries of
 * Opensoft is prohibited.
 *
 * @author Ivetta Tkachenko <ivetta.tkachenko@opensoftdev.ru>
 */

define(
    [
        'underscore',
        'backbone',
        'assets/js/modules/widgets/mailingLists/model/mailingListModel',
        'assets/js/modules/widgets/mailingLists/mailingListItemCollection',
        'assets/js/modules/utils'
    ],
    function(_, backbone, MailingListModel, MailingListItemCollection, utils) {
        var MailingListParser = function() {
            _.extend(this, backbone.Events);

            this.MIN_COLUMNS = _.keys(MailingListItemCollection.required).length;
            this.rows = [];
            this.allowedExtensions = ['csv', 'xls', 'xlsx'];
        };

        MailingListParser.prototype = {
            /**
             * @param {File} file
             */
            parseFile: function(file) {
                var _this = this;
                var columns = [];

                var options = {
                    success: function(results) {
                        if (!results.length) {
                            _this.trigger(
                                'error',
                                'Sorry, but your mailing list does not contain any addresses. Please upload a valid mailing list.'
                            );
                        }

                        _this._correctResults(results);
                        _this.rows = results;

                        columns = _this.guessColumns(results[0]);

                        _this.trigger('fileUploaded', _this.rows, columns);
                    },
                    error: function() {
                        _this.trigger('error', 'Parsing error');
                    }
                };

                var extension = file.name.slice(file.name.lastIndexOf('.') + 1);

                switch (extension) {
                    case 'csv':
                        require(['assets/js/modules/parser/csvParser'], function(csvParser) {
                            csvParser.parse(file, options);
                        });
                        break;
                    case 'xls':
                    case 'xlsx':
                        require(['assets/js/modules/parser/xlsxParser'], function(xlsxParser) {
                            xlsxParser.parse(file, options);
                        });
                        break;
                    default:
                        this.trigger('error', 'Unsupported file type!');
                        return;
                }
            },

            /**
             * @param {Array} headers
             * @returns {Array}
             */
            guessColumns: function(headers) {
                var _this = this;
                var columns = [];
                headers.forEach(function(header) {
                    var column = _this.guessColumn(header.toString());
                    column = columns.indexOf(column) === -1 ? column : null;
                    columns.push(column);
                });
                if (_.all(columns, _.isEmpty)) {
                    columns = this._fillEmptyColumns(columns);
                }
                return columns;
            },

            /**
             * @param {string} header
             * @returns {string|null}
             */
            guessColumn: function(header) {
                header = header.toLowerCase();
                if (/addr.*2/.test(header) || /2.*addr/.test(header)) {
                    return 'address2';
                }
                if (/addr/.test(header)) {
                    return 'address';
                }
                if (/state/.test(header)) {
                    return 'state';
                }
                if (/company/.test(header)) {
                    return 'company';
                }
                if (/zip/.test(header) || /postal/.test(header)) {
                    return 'postalCode';
                }
                if (/city/.test(header)) {
                    return 'city';
                }
                if (/title/.test(header) || /prefix/.test(header)) {
                    return 'title';
                }
                if (/middle/.test(header) || header === 'mi') {
                    return 'middleInitial';
                }
                if (/last/.test(header)) {
                    return 'lastName';
                }
                if (/first/.test(header) || /name/.test(header)) {
                    return 'firstName';
                }
                return null;
            },

            /**
             * @param {Array} columns
             */
            fillMailingList: function(columns) {
                var _this = this;
                var emptyRowsLength = 0;
                var list = new MailingListModel();
                var itemsCollection = list.get('itemsCollection');

                var setItem = function(index) {
                    var row = _this.rows[index];
                    if (_.every(row, _.isEmpty)) {
                        emptyRowsLength++;
                        return;
                    }
                    index = index - emptyRowsLength;
                    var item = itemsCollection.at(index);
                    if (!item) {
                        item = MailingListItemCollection.getDefaultItem({
                            orderId: index
                        });
                        itemsCollection.getItems().push(item);
                    }
                    item['loaded'] = true;
                    columns.forEach(function(column, columnIndex) {
                        if (column !== null) {
                            item[column] = row[columnIndex];
                        }
                    });
                };

                var setItemsAsync = utils.iterateAsync(0, this.rows.length, setItem, 500);

                setItemsAsync.then(function() {
                    var length = _this.rows.length - emptyRowsLength;
                    list.set('records', length);
                    list.set('currentRecords', length);
                    _this.trigger('success', list);
                });
            },

            /**
             * @param {Array} columns
             * @returns {{errors: Array}}
             */
            validateColumns: function(columns) {
                var errors = [];
                MailingListItemCollection.required.forEach(function(property) {
                    if (_.indexOf(columns, property) === -1) {
                        errors.push('You are missing the ' + MailingListItemCollection.titles[property] + ' field. Please identify it');
                    }
                });
                columns.forEach(function(column, index) {
                    if (column === null) {
                        return;
                    }
                    if (_.indexOf(_.keys(MailingListItemCollection.titles), column) === -1) {
                        errors.push('Unknown ' + column + ' field.');
                        return;
                    }
                    var propertyTitle = MailingListItemCollection.titles[column];
                    var lastIndex = _.lastIndexOf(columns, column);
                    if (lastIndex !== index) {
                        errors.push("You can't have more than 1 " + propertyTitle + ' field');
                    }
                });
                return { errors: errors };
            },

            /**
             * @param {string} extension
             * @returns {boolean}
             */
            validateExtension: function(extension) {
                return _.indexOf(this.allowedExtensions, extension) > -1;
            },

            /**
             * @param {File} file
             * @returns {{errors: Array}}
             */
            validateFile: function(file) {
                var errors = [];
                var extension = file.name.slice(file.name.lastIndexOf('.') + 1);
                if (!this.validateExtension(extension)) {
                    errors.push('Unsupported file type!');
                }
                return { errors: errors };
            },

            /**
             * @param {Array} results
             * @private
             */
            _correctResults: function(results) {
                var _this = this;
                var maxLength = MailingListModel.MAX_ITEMS_LENGTH;

                if (results[0].length < this.MIN_COLUMNS) {
                    results.forEach(function(row, i) {
                        for (var j = row.length; j < _this.MIN_COLUMNS; j++) {
                            results[i][j] = '';
                        }
                    });
                }

                if (results.length > maxLength) {
                    results = results.slice(0, maxLength);
                    this.trigger('info', 'Your mailing list exceeds ' + maxLength + ' rows. Extra rows are not processed.');
                }
            },

            /**
             * @param {Array} columns
             * @returns {Array}
             * @private
             */
            _fillEmptyColumns: function(columns) {
                if (columns.length == this.MIN_COLUMNS) {
                    columns = MailingListItemCollection.required;
                } else {
                    columns.forEach(function(column, i) {
                        columns[i] = _.keys(MailingListItemCollection.titles)[i] || null;
                    });
                }
                return columns;
            }
        };

        return MailingListParser;
    }
);
